import bugsnag from '@bugsnag/js';
import bugsnagReact from '@bugsnag/plugin-react';
import * as React from 'react';
import { ApolloProvider } from '@apollo/client';
import * as ReactDOM from 'react-dom/client';
import { ThemeProvider } from 'styled-components';
import { Normalize } from 'styled-normalize';
import { HelmetProvider } from 'react-helmet-async';
import { client } from './apollo';
import { ContextProvider, ErrorFallback, Maintenance } from './components/shared';
import { BUGSNAG_API_KEY, RELEASE_STAGE } from './consts';
import AppNavigation from './navigation/AppNavigation';
import UpgradePrompt from './components/shared/UpgradePrompt';
import * as serviceWorker from './serviceWorker';
import { theme } from './theme';
import { GlobalStyle } from './theme/GlobalStyle';
import { getPackageVersionNumber } from './utils/version';

// To replace CDN download on index.html, for offline access
// https://fonts.googleapis.com/css?family=Roboto+Condensed:300,400,700
import '@fontsource/roboto-condensed/300.css';
import '@fontsource/roboto-condensed/400.css';
import '@fontsource/roboto-condensed/700.css';
import { OnlineStatusProvider } from './hooks/useOnlineStatus';
import { SCREEN } from './theme/breakpoints';

// @ts-ignore
window.Buffer = window.Buffer || require('buffer').Buffer;

if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const isMobile = SCREEN.isMobile;
const isTablet = SCREEN.isTablet;
const isWeb = SCREEN.isWeb;

const isUnderMaintenance = false;

const bugsnagClient = bugsnag({
  apiKey: BUGSNAG_API_KEY,
  appVersion: getPackageVersionNumber(),
  releaseStage: RELEASE_STAGE,
});

bugsnagClient.use(bugsnagReact, React);
const ErrorBoundary = bugsnagClient.getPlugin('react');

const AppWithProviders = () => {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <OnlineStatusProvider>
            <ApolloProvider client={client}>
              <ContextProvider>
                {isUnderMaintenance ? (
                  <Maintenance />
                ) : (
                  <React.Fragment>
                    <Normalize />

                    <AppNavigation isMobile={isMobile} isTablet={isTablet} isWeb={isWeb} />
                    <UpgradePrompt />
                    <GlobalStyle />
                  </React.Fragment>
                )}
              </ContextProvider>
            </ApolloProvider>
          </OnlineStatusProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </HelmetProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root') as ReactDOM.Container);
root.render(<AppWithProviders />);

const updateHandler = (registration: ServiceWorkerRegistration) => {
  if (window.confirm('Update available. Do you want to reload?')) {
    window.location.reload();
  }
};
serviceWorker.register({
  onUpdate: updateHandler,
});
